import type { PointerInputEvent, SuppressionLevel } from "../core/types.ts";
import type { Mechanic, Readout } from "./mechanic.ts";

/**
 * Mechanic C — accumulated fatigue (design doc §4C).
 *
 * The screen progressively blurs as suppression builds; blinking clears it.
 * It literally simulates tear film breakup, so the metaphor teaches itself and
 * needs no tutorial copy.
 *
 * Chosen over the other two candidates for a specific reason. A ("blink as
 * reload") and B ("blackout traversal") both stand on the complete/partial
 * classification, and that classification is currently unvalidated —
 * `completeAmplitude` sits on the median of the observed amplitude
 * distribution, so which bucket a blink lands in is close to a coin flip. This
 * one consumes SuppressionLevel, whose relief is continuous in amplitude, so a
 * misclassified blink shifts the blur slightly instead of switching a state.
 *
 * It also satisfies the §7 checklist: blinking is not the scored action, the
 * primary verb is the thumb, required blink frequency never exceeds natural
 * rate, and suppressing produces an immediate legible penalty.
 */

/** Maximum blur at full suppression. Beyond this the screen is unusable. */
const MAX_BLUR_PX = 14;
/** Desaturation at full suppression — dryness reads as colour loss too. */
const MAX_DESATURATE = 0.55;

export interface JumpState {
  /** 0 = grounded, 1 = apex. Purely cosmetic; proves the thumb channel works. */
  height: number;
  jumping: boolean;
}

/**
 * Applies the effect as a CSS filter on a target element.
 *
 * A filter rather than a canvas effect so it composites over whatever is
 * underneath — the camera preview during the demo, a real scene later — without
 * the mechanic needing to know what it is blurring.
 */
export class FatigueBlur implements Mechanic {
  readonly id = "fatigue" as const;
  readonly hint = "tap anywhere to jump · blink to clear the blur";

  #suppression = 0;
  #displayed = 0;
  #jump: JumpState = { height: 0, jumping: false };
  #jumpPending = false;
  #jumpStartedAt = 0;
  #jumpCount = 0;

  constructor(private readonly target: HTMLElement) {}

  /**
   * Nothing. Deliberately, and it is not an oversight.
   *
   * `filter` on the stage applies to every descendant of it, and the HUD is one
   * — so a numeric readout of the blur level would become unreadable at exactly
   * the moment it had something to report. The blur is its own readout.
   */
  readout(): Readout | null {
    return null;
  }

  onSuppression(level: SuppressionLevel): void {
    this.#suppression = level.value;
  }

  /**
   * Arms a jump. The start time is deliberately NOT taken from the event.
   *
   * PointerInputEvent carries frame time, because it is recorded alongside the
   * signal and has to share that clock. update() runs on the render loop and
   * uses performance.now(). Mixing the two made the very first update compute
   * an elapsed time of tens of seconds, so every jump ended before it drew a
   * single frame — the animation was invisible while looking, in code, correct.
   *
   * The rule: whatever schedules an animation must read the same clock the
   * animation is stepped with.
   */
  onPointer(event: PointerInputEvent): void {
    if (event.phase !== "down" || this.#jump.jumping) return;
    this.#jumpPending = true;
  }

  /**
   * @param t render-loop time from performance.now().
   *
   * The displayed blur eases toward the target rather than snapping to it.
   * SuppressionLevel arrives at 10Hz and rendering runs faster; without easing
   * the blur would visibly step, which reads as a bug and, worse, gives the
   * player discrete moments to react to instead of a pressure that creeps.
   */
  update(t: number): void {
    this.#displayed += (this.#suppression - this.#displayed) * 0.08;

    if (this.#jumpPending) {
      this.#jumpPending = false;
      this.#jump = { height: 0, jumping: true };
      this.#jumpStartedAt = t;
      this.#jumpCount++;
    }

    if (this.#jump.jumping) {
      const elapsed = t - this.#jumpStartedAt;
      const duration = 420;
      if (elapsed >= duration) {
        this.#jump = { height: 0, jumping: false };
      } else {
        // Simple parabola: up and back down.
        const p = elapsed / duration;
        this.#jump.height = 4 * p * (1 - p);
      }
    }

    const blur = this.#displayed * MAX_BLUR_PX;
    const desaturate = this.#displayed * MAX_DESATURATE;
    this.target.style.filter =
      this.#displayed < 0.01
        ? ""
        : `blur(${blur.toFixed(2)}px) saturate(${(1 - desaturate).toFixed(2)})`;

    this.target.style.transform = this.#jump.jumping
      ? `translateY(${(-this.#jump.height * 7).toFixed(1)}%)`
      : "";
  }

  get level(): number {
    return this.#displayed;
  }

  get jump(): JumpState {
    return this.#jump;
  }

  /** Presses that have actually been rendered. The proof input is landing. */
  get jumpCount(): number {
    return this.#jumpCount;
  }

  reset(): void {
    this.#suppression = 0;
    this.#displayed = 0;
    this.#jump = { height: 0, jumping: false };
    this.#jumpPending = false;
    this.#jumpCount = 0;
    this.target.style.filter = "";
    this.target.style.transform = "";
  }
}
